<template>
  <div class="v-flex groups-list">
    <DataTable
      :actions="[]"
      :table-data="tableData"
      :busy="loading"
      :on-row-clicked="onRowClicked"
      :paginated="false"
      class-name="groups-table"
      data-cy="groups-list"
      :fixed-layout="true"
    >
      <template #cell(filters)="data">
        {{ filterLabels(data.item).join(', ') }}
      </template>

      <template #cell(schedule)="data">
        {{ scheduleLabel(data.item.automation) }}
      </template>

      <template #cell(active)="data">
        <b-form-checkbox
          switch
          class="groups-switch"
          :checked="isActive(data.item)"
          :disabled="!data.item.automation || data.item.saving"
          :title="
            data.item.automation ? '' : 'Set a template and schedule first'
          "
          :data-cy="`group-active-${data.item.id}`"
          @change="toggleActive(data.item)"
        >
          {{ isActive(data.item) ? 'Active' : 'Paused' }}
        </b-form-checkbox>
      </template>

      <template #cell(actions)="data">
        <button
          type="button"
          class="row-delete-button"
          title="Delete group"
          aria-label="Delete group"
          :data-cy="`group-delete-${data.item.id}`"
          @click="confirmDelete(data.item)"
        >
          <Trash2 :size="16" />
        </button>
      </template>
    </DataTable>
  </div>
</template>

<script lang="ts">
import DataTable from '@/components/DataTable/DataTable.vue'
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import { Condition, parseFilter } from '@/views/MessageCenter/condition.js'
import { Trash2 } from 'lucide-vue'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

/** The wire shape of core-service's group automation (GET/PUT …/automation). */
interface GroupAutomation {
  template_id: number | null
  is_active: boolean
  days_of_week: number[]
  send_time: string
}

interface GroupRow {
  id: string
  pkid: number
  name: string
  filter: Record<string, unknown>
  customer_m2m: string[]
  automation: GroupAutomation | null
  saving: boolean
}

// Human label per saved-filter key, for the Filters column. Keys are Condition.Type values.
const FILTER_LABELS: Record<string, string> = {
  [Condition.Type.Overdue]: 'Days past due',
  [Condition.Type.DueDate]: 'Days until due',
  [Condition.Type.Location]: 'Location',
  [Condition.Type.AccountManager]: 'Account manager',
  [Condition.Type.AccountStatus]: 'Account status',
  [Condition.Type.LastName]: 'Last name',
  [Condition.Type.AccountTags]: 'Tags',
  [Condition.Type.UnConfirmedPromises]: 'Unconfirmed promise',
  [Condition.Type.ConfirmedPromises]: 'Confirmed promise',
  [Condition.Type.Autopay]: 'Autopay',
  [Condition.Type.ScheduledPayment]: 'Scheduled payment',
  [Condition.Type.RegisteredCustomer]: 'Registered',
  [Condition.Type.UnregisteredCustomer]: 'Unregistered',
  [Condition.Type.CollectorAction]: 'Collector action',
  [Condition.Type.ActionDateRange]: 'Action date',
  [Condition.Type.TextEnabled]: 'Text enabled',
  [Condition.Type.EmailEnabled]: 'Email enabled',
  [Condition.Type.AiCallsEnabled]: 'AI calls enabled'
}

const DAY_ABBREVIATIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

@Component({
  components: { DataTable, Trash2 },
  computed: { ...mapGetters(['currentMerchant']) }
})
export default class GroupsList extends Vue {
  currentMerchant!: { id: string; pkid: number }

  @Prop({ type: String, default: '' }) search!: string
  @Prop({ type: Number, default: null }) openGroupPkid!: number | null

  loading = false
  groups: GroupRow[] = []

  tableData = {
    headers: [
      { key: 'name', label: 'Name', thStyle: { width: '27%' } },
      { key: 'filters', label: 'Filters', thStyle: { width: '35%' } },
      { key: 'schedule', label: 'Schedule', thStyle: { width: '16%' } },
      { key: 'active', label: 'Active', thStyle: { width: '14%' } },
      { key: 'actions', label: '', thStyle: { width: '8%' } }
    ],
    items: [] as GroupRow[]
  }

  mounted() {
    this.load()
  }

  // The whole row opens the editor, as on Templates. b-table skips clicks on the switch and the delete button.
  onRowClicked(group: GroupRow) {
    this.$emit('edit', group)
  }

  /** Groups come from Django, by UUID. Each automation comes from core-service, by group pkid. */
  async load() {
    this.loading = true
    try {
      const response = await this.axios.get(
        endpoints.listCustomerGroups(this.currentMerchant.id),
        { params: { page: 1, page_size: 1000 } }
      )
      const groups: GroupRow[] = (response.data?.results ?? []).map(
        (group: Omit<GroupRow, 'automation' | 'saving'>) => ({
          ...group,
          pkid: group.pkid,
          automation: null,
          saving: false
        })
      )
      if (
        !groups.every(
          group => Number.isSafeInteger(group.pkid) && group.pkid > 0
        )
      ) {
        throw new Error('A customer group has no pkid')
      }
      await Promise.all(
        groups.map(async group => {
          const result = await cfAxios.get(
            endpoints.groupAutomation(this.currentMerchant.pkid, group.pkid)
          )
          group.automation = result.data?.automation ?? null
        })
      )
      this.groups = groups
      this.applySearch()
      const linked = groups.find(group => group.pkid === this.openGroupPkid)
      if (linked) this.$emit('edit', linked)
    } catch {
      this.$bvToast.toast('Failed to load groups', {
        title: 'Groups',
        variant: 'danger'
      })
    } finally {
      this.loading = false
    }
  }

  applySearch() {
    const needle = this.search.trim().toLowerCase()
    this.tableData.items = needle
      ? this.groups.filter(group => group.name.toLowerCase().includes(needle))
      : this.groups
  }

  // Search is a client-side name match: the list is small and already fully loaded.
  @Watch('search')
  onSearch() {
    this.applySearch()
  }

  filterLabels(group: GroupRow): string[] {
    if (group.customer_m2m?.length) {
      return [`Customers: ${group.customer_m2m.length}`]
    }

    return parseFilter(group.filter).map((condition: Condition) => {
      const { min, max } = condition as {
        min?: number | null
        max?: number | null
      }
      // Single-day ranges (every converted reminder) read as the day itself.
      if (condition.type === Condition.Type.DueDate) {
        return (condition as { text: string }).text
      }
      if (condition.type === Condition.Type.Overdue && min && min === max) {
        return `Days past due: ${min}`
      }
      const label = FILTER_LABELS[condition.type] ?? condition.type
      const value = (condition as { short?: string | null }).short

      return value ? `${label}: ${value}` : label
    })
  }

  isActive(group: GroupRow): boolean {
    return Boolean(group.automation?.is_active)
  }

  scheduleLabel(automation: GroupAutomation | null): string {
    if (!automation?.days_of_week?.length) return '—'
    const days = [...automation.days_of_week].sort()
    const is = (expected: number[]) =>
      days.length === expected.length && days.every((d, i) => d === expected[i])
    if (is([0, 1, 2, 3, 4, 5, 6])) return 'Daily'
    if (is([0, 1, 2, 3, 4])) return 'Weekdays'
    if (is([5, 6])) return 'Weekends'

    return days.map(day => DAY_ABBREVIATIONS[day]).join(', ')
  }

  /** The switch is a PUT of the stored automation with is_active flipped; nothing else changes. */
  async toggleActive(group: GroupRow) {
    if (!group.automation) return
    group.saving = true
    const body = { ...group.automation, is_active: !group.automation.is_active }
    try {
      const result = await cfAxios.put(
        endpoints.groupAutomation(this.currentMerchant.pkid, group.pkid),
        body
      )
      group.automation = result.data?.automation ?? body
    } catch {
      this.$bvToast.toast('Failed to update the group', {
        title: 'Groups',
        variant: 'danger'
      })
    } finally {
      group.saving = false
    }
  }

  async confirmDelete(group: GroupRow) {
    const ok = await this.$bvModal.msgBoxConfirm(
      `Delete "${group.name}"? Its automation stops with it.`,
      { title: 'Delete group', okVariant: 'danger', okTitle: 'Delete' }
    )
    if (!ok) return
    try {
      await this.axios.delete(
        endpoints.updateCustomerGroup(this.currentMerchant.id, group.id)
      )
      this.groups = this.groups.filter(row => row.id !== group.id)
      this.applySearch()
    } catch {
      this.$bvToast.toast('Failed to delete the group', {
        title: 'Groups',
        variant: 'danger'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// ::v-deep leads: the table sits inside DataTable, so it never carries this component's scope id.
::v-deep .groups-table {
  tbody tr {
    cursor: pointer;
  }

  // The switch and the delete button are a hair taller than a line of text; centre every cell on them.
  td {
    vertical-align: middle;
  }
}

// Bootstrap's stock switch, the same one the Merchant Settings pages use.
// Table.vue's global cell rule sets overflow: hidden on everything in a td, which clips the track and knob
// Bootstrap draws outside the label. It exempts .custom-checkbox labels only, not switches.
.groups-switch ::v-deep .custom-control-label {
  overflow: visible;

  cursor: pointer;
}

.row-delete-button {
  padding: 4px;
  color: $darker-gray;

  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    color: $danger;
  }
}
</style>
